import Utilities from "./Utilities";
import Wasserstand from "./Wasserstand";
import Modal from "./Modal";
import Veraportal from "./Veraportal";

export interface IFilterRule {
    field: string;
    operator: string;
    value: string | number
}

export interface IFilterCategory {
    category: string;
    labelCaption?: string;
    dataType: string;
    staticSortingOrder?: string[];
}

export interface ISortingRules {
    category: string;
    direction?: string;
}

export interface ISavedFilterSet {
    name: string;
    filterRules: IFilterRule[];
    sortingRules: ISortingRules[];
}

export interface ITableFilterOptions {
    tableId: string;
    filterSlot?: HTMLDivElement;
    filterCategories: IFilterCategory[];
    defaultFilterRules?: IFilterRule[];
    defaultSortingRules: ISortingRules[];
    predefinedFilterSets?: ISavedFilterSet[];
}

export class TableFilter {
    get sortingRules(): ISortingRules[] {
        return this._sortingRules;
    }

    set sortingRules(value: ISortingRules[]) {
        this._sortingRules = value;
        sessionStorage.setItem(`${this.tableId}-sortingRules`, JSON.stringify(value));
    }

    get filterRules(): IFilterRule[] {
        return this._filterRules;
    }

    set filterRules(value: IFilterRule[]) {
        this._filterRules = value;
        sessionStorage.setItem(`${this.tableId}-filterRules`, JSON.stringify(value));
    }

    private _veraportal: Veraportal;
    private tableId: string;
    private filterSlot: HTMLDivElement;
    private filterCategories: IFilterCategory[] = [];
    private _filterRules: IFilterRule[] = [];
    private _sortingRules: ISortingRules[] = [];
    private _predefinedFilterSets: ISavedFilterSet[] = [];

    constructor(parent: Veraportal, options: ITableFilterOptions) {
        this._veraportal = parent;
        this.tableId = options.tableId;
        this._predefinedFilterSets = options.predefinedFilterSets ?? [];

        const storedFilterRules = sessionStorage.getItem(`${this.tableId}-filterRules`);
        this.filterRules = storedFilterRules ? JSON.parse(storedFilterRules) : options.defaultFilterRules ?? [];

        const storedSortingRules = sessionStorage.getItem(`${this.tableId}-sortingRules`);
        this.sortingRules = storedSortingRules ? JSON.parse(storedSortingRules) : options.defaultSortingRules ?? [];

        this.filterCategories = options.filterCategories ?? [];

        if (options.filterSlot !== undefined) {
            this.filterSlot = options.filterSlot;
            this.renderFilterUI();
            this.initUI();
            this.refreshActiveFilters();
        }
    }

    private renderFilterUI(): void {
        this.filterSlot.innerHTML += `
            <div class="filter-builder" data-ausgeklappt="0">
                <h3>🢒 Filtereinstellungen</h3>
                <div>
                    <div class="filter-rules">
                        <h4>Aktive Filter</h4>
                        <div class="active-rules"></div>
                        <div class="new rule">
                            <div class="field">
                                <select id="${this.tableId}-field-select" class="small">
        ${this.filterCategories.map(
            c => `<option value="${c.category}">${(c.labelCaption !== undefined) ? `${c.labelCaption}` : `${Utilities.capitalize(c.category)}`}</option>`
        ).join('\n')}
                                </select>
                            </div>
                            <div class="operator">
                                <select id="${this.tableId}-operator-select" class="small">
                                    <option value="equals">gleich (=)</option>
                                    <option value="notEquals">nicht gleich (≠)</option>
                                    <option value="greaterThan">größer als (>)</option>
                                    <option value="lessThan">kleiner als (<)</option>
                                    <option value="contains">enthält</option>
                                </select>
                            </div>
                            <div class="input">
                                <input type="text" id="${this.tableId}-value-input" class="small" placeholder="Wert..."/>
                            </div>
                            <div class="button">
                                <button id="${this.tableId}-add-filter-btn" class="small icon-only secondary">
                                    <svg role="graphics-symbol">
                                        <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#plus-circle-fill"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="sorting-rules">
                        <h4>Sortierung</h4>
                        <div>
                            <div class="sorting-category">
                                <select id="${this.tableId}-sorting-category-select" class="small">
        ${this.filterCategories.map(
            c => `<option value="${c.category}">${(c.labelCaption !== undefined) ? `${c.labelCaption}` : `${Utilities.capitalize(c.category)}`}</option>`
        ).join('\n')}
                                </select>
                            </div>
                            <div class="sorting-order">
                                <select id="${this.tableId}-sorting-order" class="small">
                                    <option value="asc">aufsteigend</option>
                                    <option value="desc">absteigend</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="options">
                        <button id="${this.tableId}-reset-filters-btn" class="secondary small">
                            <svg role="graphics-symbol">
                                <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#x-lg"/>
                            </svg>
                            Filter zurücksetzen
                        </button>
                        ${(false) ? `
                        <button id="${this.tableId}-filter-save-btn" class="disabled secondary small">
                            <svg role="graphics-symbol">
                                <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#floppy"/>
                            </svg>
                            Filter speichern
                        </button>
                        ` : ``}
                    </div>
                </div>
            </div>
            <div class="filter-saved-sets">
                    <h4>Gespeicherte Filter</h4>
                    <div>
                        <div class="saved-filters-select"><select id="${this.tableId}-saved-filters-select" class=""></select></div>
                        <button id="${this.tableId}-filter-load-btn" class="secondary">
                            <svg role="graphics-symbol">
                                <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#check-lg"/>
                            </svg>
                            Filter anwenden
                        </button>
                        ${(false) ? `
                        <button id="${this.tableId}-filter-delete-btn" class="secondary">
                            <svg role="graphics-symbol">
                                <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#trash3"/>
                            </svg>
                            gespeicherten Filter löschen
                        </button>
                        ` : ``}
                    </div>
                </div>
        `;
    }

    private saveFilters(): void {
        const name = prompt("Bitte geben Sie einen Namen für dieses Filterset ein:");
        if (!name) return;

        if (this._predefinedFilterSets.some(set => set.name === name)) {
            alert("Dieser Name wird bereits von einem vordefinierten Filter verwendet. Bitte wählen Sie einen anderen.");
            return;
        }

        const newFilterSet: ISavedFilterSet = {
            name: name,
            filterRules: this.filterRules,
            sortingRules: this.sortingRules
        };

        const storageKey = `${this.tableId}-savedFilters`;
        let savedFilters: ISavedFilterSet[] = [];
        try {
            const storedFilters = localStorage.getItem(storageKey);
            if (storedFilters) {
                savedFilters = JSON.parse(storedFilters);
            }
        } catch (e) {
            Utilities.errorToSentry(e);
            console.error("Fehler beim Laden der gespeicherten Filter:", e);
        }

        // Duplikate entfernen (basierend auf dem Namen) und neues Set hinzufügen
        savedFilters = savedFilters.filter(set => set.name !== name);
        savedFilters.push(newFilterSet);

        localStorage.setItem(storageKey, JSON.stringify(savedFilters));
        alert(`Das Filterset '${name}' wurde gespeichert.`);
        this.populateSavedFiltersDropdown();
    }

    private async loadFilters(): Promise<void> {
        const select = document.querySelector(`#${this.tableId}-saved-filters-select`) as HTMLSelectElement;
        const name: string = select?.value ?? null;
        if (name === null)
            return;

        let filterSetToLoad = this._predefinedFilterSets.find(set => set.name === name);

        if (!filterSetToLoad) {
            const storageKey = `${this.tableId}-savedFilters`;
            const storedFilters = localStorage.getItem(storageKey);
            const savedFilters: ISavedFilterSet[] = storedFilters ? JSON.parse(storedFilters) : [];
            filterSetToLoad = savedFilters.find(set => set.name === name);
        }

        if (filterSetToLoad) {
            this.filterRules = filterSetToLoad.filterRules;
            this.sortingRules = filterSetToLoad.sortingRules;

            this.refreshActiveFilters();

            // UI für Sortierung aktualisieren
            const categorySelect = document.querySelector(`#${this.tableId}-sorting-category-select`) as HTMLSelectElement;
            const orderSelect = document.querySelector(`#${this.tableId}-sorting-order`) as HTMLSelectElement;
            if (this.sortingRules.length > 0) {
                const rule = this.sortingRules[0];
                // @ts-ignore
                if (categorySelect.tomselect) {
                    // @ts-ignore
                    await categorySelect.tomselect.setValue(rule.category, true);
                }
                // @ts-ignore
                if (orderSelect.tomselect) {
                    // @ts-ignore
                    await orderSelect.tomselect.setValue(rule.direction, true);
                }
            }

            // Filter anwenden und Tabelle aktualisieren
            if (this.tableId === 'wasserstand') {
                await (this._veraportal as Wasserstand).refresh();
                let hinweisModal = new Modal(this._veraportal);
                await hinweisModal.show({
                    title: `Filterset geladen`,
                    body: `<p>Filterset <i>'${name}'</i> geladen.</p>`,
                    hasSubmit: false,
                    hasCancel: true,
                    hasBestaetigung: false,
                    width: 500,
                    cancelText: 'OK'
                });
            }
        } else {
            let hinweisModal = new Modal(this._veraportal);
            await hinweisModal.show({
                title: `Filterset nicht gefunden`,
                body: `<p>Filterset konnte nicht gefunden werden.</p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 300,
                cancelText: 'OK'
            });
        }
    }

    private deleteFilters(): void {
        const select = document.querySelector(`#${this.tableId}-saved-filters-select`) as HTMLSelectElement;
        const name = select.value;

        if (!name) return;

        const isPredefined = this._predefinedFilterSets.some(set => set.name === name);
        if (isPredefined) {
            alert("Vordefinierte Filter können nicht gelöscht werden.");
            return;
        }

        if (!confirm(`Möchten Sie das Filterset '${name}' wirklich löschen?`)) {
            return;
        }

        const storageKey = `${this.tableId}-savedFilters`;
        const storedFilters = localStorage.getItem(storageKey);
        const savedFilters: ISavedFilterSet[] = storedFilters ? JSON.parse(storedFilters) : [];

        const updatedFilters = savedFilters.filter(set => set.name !== name);
        localStorage.setItem(storageKey, JSON.stringify(updatedFilters));

        this.populateSavedFiltersDropdown();
        alert(`Filterset '${name}' wurde gelöscht.`);
    }

    private populateSavedFiltersDropdown(): void {
        const select = document.querySelector(`#${this.tableId}-saved-filters-select`) as HTMLSelectElement;
        if (!select) return;

        // @ts-ignore
        if (select.tomselect) {
            // @ts-ignore
            select.tomselect.destroy();
        }

        const storageKey = `${this.tableId}-savedFilters`;
        const storedFilters = localStorage.getItem(storageKey);
        const savedFilters: ISavedFilterSet[] = storedFilters ? JSON.parse(storedFilters) : [];

        select.innerHTML = ''; // Clear previous options

        if (this._predefinedFilterSets.length === 0 && savedFilters.length === 0) {
            select.innerHTML = '<option value="">Keine Filter gespeichert</option>';
            (document.querySelector(`#${this.tableId}-filter-load-btn`) as HTMLButtonElement).disabled = true;
            (document.querySelector(`#${this.tableId}-filter-delete-btn`) as HTMLButtonElement).disabled = true;
        } else {
            if (this._predefinedFilterSets.length > 0) {
                const predefinedGroup = document.createElement('optgroup');
                predefinedGroup.label = 'Vordefinierte Filter';
                this._predefinedFilterSets.forEach(set => {
                    const option = document.createElement('option');
                    option.value = set.name;
                    option.textContent = set.name;
                    predefinedGroup.appendChild(option);
                });
                select.appendChild(predefinedGroup);
            }

            if (savedFilters.length > 0) {
                const userGroup = document.createElement('optgroup');
                userGroup.label = 'Meine Filter';
                savedFilters.forEach(set => {
                    const option = document.createElement('option');
                    option.value = set.name;
                    option.textContent = set.name;
                    userGroup.appendChild(option);
                });
                select.appendChild(userGroup);
            }

            try {
                (document.querySelector(`#${this.tableId}-filter-load-btn`) as HTMLButtonElement).disabled = false;
                (document.querySelector(`#${this.tableId}-filter-delete-btn`) as HTMLButtonElement).disabled = false;
            } catch {
                //
            }

        }

        // @ts-ignore
        new TomSelect(select, {
            create: false,
            allowEmptyOption: true,
            controlInput: null,
            searchField: []
        });
    }

    private initUI(): void {

        // @ts-ignore
        new TomSelect(`#${this.tableId}-field-select`, {
            create: false,
            allowEmptyOption: false,
            controlInput: null,
            searchField: [],
        });

        // @ts-ignore
        new TomSelect(`#${this.tableId}-operator-select`, {
            create: false,
            allowEmptyOption: false,
            controlInput: null,
            searchField: [],
        });

        // @ts-ignore
        new TomSelect(`#${this.tableId}-sorting-category-select`, {
            create: false,
            allowEmptyOption: false,
            controlInput: null,
            searchField: [],
        });

        // @ts-ignore
        new TomSelect(`#${this.tableId}-sorting-order`, {
            create: false,
            allowEmptyOption: false,
            controlInput: null,
            searchField: [],
        });

        document.querySelector(`.filter-builder`)
            ?.addEventListener('click', (e) => {
                let target = (e.target as HTMLElement);
                if (target.closest(`h3`) === null)
                    return;

                let filterBuilderDiv = target.closest(`div.filter-builder[data-ausgeklappt]`);
                filterBuilderDiv.setAttribute(
                    'data-ausgeklappt',
                    (filterBuilderDiv.getAttribute('data-ausgeklappt') === '0') ? '1' : '0'
                );
            });

        document.querySelector(`#${this.tableId}-add-filter-btn`)
            ?.addEventListener('click', () => {
                let field: string = (document.querySelector(`#${this.tableId}-field-select`) as HTMLSelectElement)?.value ?? null;
                let operator: string = (document.querySelector(`#${this.tableId}-operator-select`) as HTMLSelectElement)?.value ?? null;
                let value: string = (document.querySelector(`#${this.tableId}-value-input`) as HTMLSelectElement)?.value ?? null;

                if (field.length === 0 || field === null)
                    return;
                if (operator.length === 0 || operator === null)
                    return;
                if (value.length === 0 || value === null)
                    return;

                const newRules = this.filterRules.filter(r => r.field !== field);
                newRules.push(
                    {field: field, operator: operator, value: value}
                );
                this.filterRules = newRules;

                this.refreshActiveFilters();

                if (this.tableId === 'wasserstand')
                    (this._veraportal as Wasserstand).refresh().then();
            });

        document.querySelector(`#${this.tableId}-reset-filters-btn`)
            ?.addEventListener('click', () => {
                this.filterRules = [];
                this.refreshActiveFilters();

                if (this.tableId === 'wasserstand')
                    (this._veraportal as Wasserstand).refresh().then();
            });

        document.querySelector(`#${this.tableId}-filter-save-btn`)
            ?.addEventListener('click', () => {
                this.saveFilters();
            });

        document.querySelector(`#${this.tableId}-filter-load-btn`)
            ?.addEventListener('click', () => {
                this.loadFilters();
            });

        document.querySelector(`#${this.tableId}-filter-delete-btn`)
            ?.addEventListener('click', () => {
                this.deleteFilters();
            });

        const sortingCategorySelect = document.querySelector(`#${this.tableId}-sorting-category-select`) as HTMLSelectElement;
        const sortingOrderSelect = document.querySelector(`#${this.tableId}-sorting-order`) as HTMLSelectElement;

        const updateSortingAndRefresh = () => {
            this.sortingRules = [{
                category: sortingCategorySelect.value,
                direction: sortingOrderSelect.value
            }];

            if (this.tableId === 'wasserstand')
                (this._veraportal as Wasserstand).refresh().then();
        };

        sortingCategorySelect.addEventListener('change', updateSortingAndRefresh);
        sortingOrderSelect.addEventListener('change', updateSortingAndRefresh);

        this.populateSavedFiltersDropdown();
    }

    public addFilterRuleAndRefresh(rule: IFilterRule): void {
        const newRules = this.filterRules.filter(r => r.field !== rule.field);
        newRules.push(rule);
        this.filterRules = newRules;

        this.refreshActiveFilters();

        if (this.tableId === 'wasserstand') {
            (this._veraportal as Wasserstand).refresh().then();
        }
    }

    public filterAndSortData<T>(dataArr: T[]): T[] {
        let filteredDataArr: T[] = Utilities.cloneObject(dataArr);

        // if (this.filterRules.length === 0 && this.sortingRules.length === 0)
        //     return filteredDataArr;

        filteredDataArr = filteredDataArr
            .filter(row => {
                return this._filterRules.every(
                    rule => this.checkRule(row, rule)
                );
            });

        if (this._sortingRules.length > 0) {
            filteredDataArr.sort((a: any, b: any): number => {
                // This loop enables multi-level sorting.
                for (const rule of this._sortingRules) {
                    const key = rule.category;
                    const direction = rule.direction ?? 'asc';
                    const categoryInfo = this.filterCategories.find(c => c.category === key);

                    const valA = a[key];
                    const valB = b[key];

                    let comparison = 0;

                    if (valA == null && valB == null) {
                        comparison = 0;
                    } else if (valA == null) {
                        return 1; // a is null, goes after b
                    } else if (valB == null) {
                        return -1; // b is null, goes after a
                    }

                    const staticOrder = categoryInfo?.staticSortingOrder;
                    if (staticOrder && staticOrder.length > 0) {
                        const indexA = staticOrder.indexOf(String(valA));
                        const indexB = staticOrder.indexOf(String(valB));

                        if (indexA !== -1 && indexB !== -1) {
                            // Case 1: Both values are in the static list. Sort by their index.
                            comparison = indexA - indexB;
                        } else if (indexA !== -1) {
                            // Case 2: Only A is in the list. A comes first.
                            comparison = -1;
                        } else if (indexB !== -1) {
                            // Case 3: Only B is in the list. B comes first.
                            comparison = 1;
                        } else {
                            // Case 4: Neither is in the list. Sort them alphabetically against each other.
                            comparison = String(valA).localeCompare(String(valB));
                        }
                    } else {
                        const dataType = categoryInfo ? categoryInfo.dataType : 'string';
                        switch (dataType) {
                            case 'number':
                                comparison = valA - valB;
                                break;
                            case 'boolean':

                                comparison = (valA === valB) ? 0 : valA ? 1 : -1; // This will sort `false` before `true` in ascending order
                                break;
                            case 'string':
                            default:
                                comparison = String(valA).localeCompare(String(valB));
                                break;
                        }
                    }

                    if (comparison !== 0) {
                        return direction === 'desc' ? comparison * -1 : comparison;
                    }
                }
                return 0;
            });
        }

        return filteredDataArr;
    }

    private checkRule(item: unknown, rule: IFilterRule) {
        const itemValue = item[rule.field];
        const ruleValue = rule.value;
        switch (rule.operator) {
            case 'equals':
                return String(itemValue) === String(ruleValue);
            case 'notEquals':
                return itemValue !== ruleValue;
            case 'contains':
                return String(itemValue).toLowerCase().includes(String(ruleValue).toLowerCase()); // Ensure we're dealing with strings before calling string methods
            case 'greaterThan':
                return itemValue > ruleValue;
            case 'lessThan':
                return itemValue < ruleValue;
            default:
                return true;
        }
    }

    private bezeichnungFromRule(operator: string): string {
        switch (operator) {
            case 'equals':
                return 'gleich (=)';
            case 'notEquals':
                return 'nicht gleich (≠)';
            case 'contains':
                return 'enthält';
            case 'greaterThan':
                return 'größer als (>)';
            case 'lessThan':
                return 'kleiner als (<)';
        }
    }

    public refreshActiveFilters(): void {
        document.querySelector(`div.active-rules`).innerHTML = this._filterRules
            .map(r => `
                    <div class="rule">
                        <div class="field">${Utilities.capitalize(r.field)}</div>
                        <div class="operator">${this.bezeichnungFromRule(r.operator)}</div>
                        <div class="value">'${r.value}'</div>
                    </div>
                `).join('');
    }

}
